/**
 * Shared monograph panel color tokens derived from the CSK demo design system.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { DEMO_COLOR, FONT, GENERATOR_COLOR, MODE_COLOR } from "../design-system/tokens.js";
import type { MonographPanelTokens } from "./MonographPanelPrimitiveTypes.js";

export const SHARED_MONOGRAPH_PANEL_TOKENS = {
  void: "#04060f",
  surface: "#0a1325",
  elev: "#13203b",
  border: "#27345a",
  muted: "#7a8aa6",
  text: "#cfe2f5",
  bright: "#eaf5ff",
  accent: DEMO_COLOR.cyan,
  accentAmber: DEMO_COLOR.amber,
  accentBlue: DEMO_COLOR.sky,
  accentCyan: DEMO_COLOR.cyan,
  accentDanger: DEMO_COLOR.danger,
  accentEmerald: DEMO_COLOR.emerald,
  accentForbidden: DEMO_COLOR.danger,
  accentGold: DEMO_COLOR.gold,
  accentGreen: DEMO_COLOR.success,
  accentIndigo: DEMO_COLOR.indigo,
  accentMagenta: DEMO_COLOR.magenta,
  accentOrange: DEMO_COLOR.orange,
  accentPink: DEMO_COLOR.pink,
  accentPurple: DEMO_COLOR.violet,
  accentRed: DEMO_COLOR.danger,
  accentRose: DEMO_COLOR.rose,
  accentSafe: DEMO_COLOR.success,
  accentSlate: DEMO_COLOR.slate,
  accentTeal: DEMO_COLOR.teal,
  accentViolet: DEMO_COLOR.violet,
  accentYellow: DEMO_COLOR.gold,
  axisG1: GENERATOR_COLOR.G1.primary,
  axisG2: GENERATOR_COLOR.G2.primary,
  axisG3: GENERATOR_COLOR.G3.primary,
  axisG4: GENERATOR_COLOR.G4.primary,
  contained: MODE_COLOR.contained.primary,
  g1: GENERATOR_COLOR.G1.primary,
  g2: GENERATOR_COLOR.G2.primary,
  g3: GENERATOR_COLOR.G3.primary,
  g4: GENERATOR_COLOR.G4.primary,
  immune: MODE_COLOR.immune.primary,
  inert: MODE_COLOR.inert.primary,
  mono: FONT.mono,
  sans: FONT.body,
} as const;

export type SharedMonographPanelToken = keyof typeof SHARED_MONOGRAPH_PANEL_TOKENS;

export function createMonographPanelTokens<TTokens extends Record<string, string>>(
  appTokens: TTokens,
): MonographPanelTokens & typeof SHARED_MONOGRAPH_PANEL_TOKENS & TTokens {
  return {
    ...SHARED_MONOGRAPH_PANEL_TOKENS,
    ...appTokens,
  };
}

export type StandardMonographPanelTokens = MonographPanelTokens &
  typeof SHARED_MONOGRAPH_PANEL_TOKENS &
  Record<string, string>;

const STANDARD_PANEL_TOKEN_FALLBACKS = createMonographPanelTokens({
  void: "#04060f",
  surface: "#0a1325",
  elev: "#13203b",
  border: "#27345a",
  muted: "#7a8aa6",
  text: "#cfe2f5",
  bright: "#eaf5ff",
  accent: DEMO_COLOR.cyan,
});

const STANDARD_PANEL_TOKEN_SOURCE = STANDARD_PANEL_TOKEN_FALLBACKS as StandardMonographPanelTokens;

function semanticTokenFallback(name: string): string {
  const lower = name.toLowerCase();
  if (/(red|danger|forbidden|blocked|violation|adversary)/u.test(lower)) {
    return DEMO_COLOR.danger;
  }
  if (/(green|safe|immune|honest|success|distill)/u.test(lower)) {
    return DEMO_COLOR.success;
  }
  if (/(amber|gold|yellow|budget|time|contained|singular)/u.test(lower)) {
    return DEMO_COLOR.gold;
  }
  if (/(purple|violet|magenta|pink|identity|consistency|coalgebra)/u.test(lower)) {
    return DEMO_COLOR.violet;
  }
  if (/(blue|cyan|teal|reach|lineage|bridge|boundary|context)/u.test(lower)) {
    return DEMO_COLOR.sky;
  }
  if (/(orange|authority|rewrite|seam|origin|evidence)/u.test(lower)) {
    return DEMO_COLOR.orange;
  }
  return STANDARD_PANEL_TOKEN_FALLBACKS.accent;
}

export const standardMonographPanelTokens: StandardMonographPanelTokens = new Proxy(
  STANDARD_PANEL_TOKEN_SOURCE,
  {
    get(target, property): string | undefined {
      if (typeof property !== "string") {
        return undefined;
      }
      return Object.prototype.hasOwnProperty.call(target, property)
        ? target[property]
        : semanticTokenFallback(property);
    },
  },
);
